'use client';

import { Card } from '@/components/ui';
import { ScrollableContainer } from '../common/scrollable-container';
import { ScatterPlot } from '../common/scatter-plot';
import jStat from 'jstat';

export interface CovarianceResult {
  covariance: number;
  sampleSize: number;
  data1: number[];
  data2: number[];
  xLabel?: string;
  yLabel?: string;
  mean1: number;
  mean2: number;
}

interface CovarianceCalculatorProps {
  visualizationData: CovarianceResult | null;
}

export function CovarianceCalculator({ visualizationData: result }: CovarianceCalculatorProps) {
  if (!result) {
    return null;
  }

  const { covariance, sampleSize, data1, data2, xLabel, yLabel, mean1, mean2 } = result;

  // Create scatter plot data points
  const scatterData = data1.map((x, i) => [x, data2[i]] as [number, number]);

  return (
    <ScrollableContainer>
      <div className="space-y-6">
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Covariance analysis results</h3>
          <div className="space-y-4">
            <div>
              <p className="font-medium">Covariance:</p>
              <p>{covariance.toFixed(4)}</p>
            </div>
            <div>
              <p className="font-medium">Sample size:</p>
              <p>{sampleSize}</p>
            </div>
            <div>
              <p className="font-medium">Mean of {xLabel || 'Variable 1'}:</p>
              <p>{mean1.toFixed(4)}</p>
            </div>
            <div>
              <p className="font-medium">Mean of {yLabel || 'Variable 2'}:</p>
              <p>{mean2.toFixed(4)}</p>
            </div>
            <div>
              <p className="font-medium">Statistical interpretation:</p>
              <div className="space-y-2 mt-1">
                <p className="text-sm text-gray-600">
                  Direction:
                  <span className="text-black font-medium ml-2">
                    {covariance > 0
                      ? 'Positive covariance (variables tend to move in the same direction)'
                      : covariance < 0
                      ? 'Negative covariance (variables tend to move in opposite directions)'
                      : 'Zero covariance (no linear relationship)'}
                  </span>
                </p>
                <p className="text-sm text-gray-600">
                  Covariance measures how two variables change together, but its magnitude depends on the scales of the variables. 
                  For a scale-independent measure of relationship strength, consider using the correlation coefficient.
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Scatter plot</h3>
          <ScatterPlot
            data={scatterData}
            xLabel={xLabel || 'Variable 1'}
            yLabel={yLabel || 'Variable 2'}
            title={`Covariance Plot (cov = ${covariance.toFixed(4)})`}
          />
        </Card>
      </div>
    </ScrollableContainer>
  );
} 